"use strict";
import Account from "../../api/account";
export class Path {
  constructor(basePath) {
    this.basePath = basePath || [];
    this.queries = [];
  }
  appendToBase(strings) {
    this.basePath = this.basePath.concat(strings);
    return this;
  }
  addQueries(newQueries) {
    this.queries = this.queries.concat(newQueries);
    return this;
  }
  toString() {
    return this.pathString() + this.queryString();
  }
  pathString() {
    return [""].concat(this.basePath).join("/");
  }
  queryString() {
    const qs = this.queries.map((x) => x[0] + "=" + x[1]).join("&");
    return qs ? "?" + qs : "";
  }
  queryObj() {
    const query = {};
    this.queries.forEach((value) => {
      query[value[0]] = value[1];
    });
    return query;
  }
}
export function submissionReview(emailMessageId) {
  return submissionPath(emailMessageId).appendToBase(["review"]);
}
export function submissionWithInvoicePath(emailMessageId, factorInvoiceId) {
  return submissionPath(emailMessageId).appendToBase(["invoice", factorInvoiceId.toString()]);
}
export function submissionWithGroupPath(emailMessageId, groupId) {
  return submissionPath(emailMessageId).addQueries([["group", groupId.toString()]]);
}
export function submissionPath(emailMessageId) {
  return new Path(["client_submission", "email", emailMessageId.toString()]);
}
export function carrierInvoiceWithLoadPath(loadStateId, carrierInvoiceId, invoiceRequiresLtlReconciliation) {
  const rootPath = loadPath(loadStateId).appendToBase(["carrier_invoices", carrierInvoiceId.toString()]);
  if (invoiceRequiresLtlReconciliation) {
    return rootPath.appendToBase(["ltl-reconcile"]);
  } else {
    return rootPath;
  }
}
export function carrierInvoiceFromReplyPath(loadStateId, carrierInvoiceId, replyId, invoiceRequiresLtlReconciliation) {
  return carrierInvoiceWithLoadPath(loadStateId, carrierInvoiceId, invoiceRequiresLtlReconciliation).addQueries([
    ["activeDocumentTab", "Message"],
    ["emailId", replyId.toString()]
  ]);
}
export function loadPath(loadStateId) {
  return new Path(["loads", loadStateId.toString()]);
}
export function replyMessagePath(emailMessageId) {
  return new Path(["reply", emailMessageId.toString()]);
}
export function emailMessagePath(emailMessageId) {
  return new Path(["email", emailMessageId.toString()]);
}
export function forwardingEmailMessagePath(emailMessageId) {
  return new Path(["forwarding", "email_messages", emailMessageId.toString()]);
}
export function documentBatchWithEmailPath(documentBatchId, emailMessageId) {
  return documentBatchPath(documentBatchId).addQueries([["emailId", emailMessageId.toString()]]);
}
export function documentGroupPath(documentBatchId, documentGroupId) {
  return documentBatchPath(documentBatchId).addQueries([["group_id", documentGroupId.toString()]]);
}
export function documentBatchPath(documentBatchId) {
  return new Path(["index-document-batches", documentBatchId.toString()]);
}
export function documentBatchWithLoadPath(documentBatchId, loadStateId) {
  return documentBatchPath(documentBatchId).appendToBase(["loads", loadStateId.toString()]);
}
export function nonInvoiceConfirmPath(documentBatchId, loadStateId) {
  return documentBatchWithLoadPath(documentBatchId, loadStateId).appendToBase(["confirm"]);
}
export function factorInvoice(factorInvoiceId) {
  return new Path(["factor_invoices", factorInvoiceId.toString()]);
}
export function factorInvoiceFromReply(factorInvoiceId, replyId) {
  return factorInvoice(factorInvoiceId).addQueries([
    ["activeDocumentTab", "Message"],
    ["emailId", replyId.toString()]
  ]);
}
export function factorEmail(useSubmissionWorkflow, emailMessageId, emailHasFactorClientInvoiceSubmission, documentBatchId) {
  if (useSubmissionWorkflow && emailHasFactorClientInvoiceSubmission) {
    return submissionPath(emailMessageId);
  } else {
    if (documentBatchId) {
      return documentBatchPath(documentBatchId);
    } else {
      return emailMessagePath(emailMessageId);
    }
  }
}
export function pathToEmail(emailParams) {
  return Account.matchAccountType({
    broker: () => {
      return invoiceIndexPath(emailParams);
    },
    factor: () => {
      return submissionOrInvoicePath(Account.useSubmissionWorkflow(), Account.useBothSubmissionWorkflows(), emailParams);
    },
    forwarder: () => {
      return forwardingEmailMessagePath(emailParams.emailMessageId);
    }
  });
}
export function submissionOrInvoicePath(useSubmissionWorkflow, useBothWorkflows, emailParams) {
  if (useSubmissionWorkflow && !useBothWorkflows) {
    return submissionPath(emailParams.emailMessageId);
  } else if (useBothWorkflows && emailParams.hasSubmission) {
    return submissionPath(emailParams.emailMessageId);
  } else {
    return invoiceIndexPath(emailParams);
  }
}
export function invoiceIndexPath(emailParams) {
  if (emailParams.maybeConformingSubmission) {
    return carrierInvoiceWithLoadPath(
      emailParams.maybeConformingSubmission.loadStateId,
      emailParams.maybeConformingSubmission.carrierInvoice.invoiceId,
      emailParams.maybeConformingSubmission.carrierInvoice.requiresLtlReconciliation
    );
  }
  if (emailParams.maybeInvoiceOwnerParams && emailParams.maybeDocumentBatchId) {
    return invoiceOwnerPath(emailParams, emailParams.maybeInvoiceOwnerParams);
  } else {
    return pathToMaybeDocumentBatch(emailParams);
  }
}
function invoiceOwnerPath(emailParams, invoiceOwnerParams) {
  switch (invoiceOwnerParams.kind) {
    case "carrier":
      return carrierInvoiceFromReplyPath(
        invoiceOwnerParams.loadStateId,
        invoiceOwnerParams.carrierInvoice.invoiceId,
        emailParams.emailMessageId,
        invoiceOwnerParams.carrierInvoice.requiresLtlReconciliation
      );
    case "factor":
      return factorInvoiceFromReply(invoiceOwnerParams.factorInvoiceId, emailParams.emailMessageId);
  }
}
function pathToMaybeDocumentBatch(emailParams) {
  if (emailParams.maybeDocumentBatchId) {
    const queries = isReply(emailParams.maybeEmailType) ? [["fromReply", "true"]] : [];
    return documentBatchWithEmailPath(emailParams.maybeDocumentBatchId, emailParams.emailMessageId).addQueries(queries);
  } else {
    if (isReply(emailParams.maybeEmailType)) {
      return replyMessagePath(emailParams.emailMessageId);
    } else {
      return emailMessagePath(emailParams.emailMessageId);
    }
  }
}
function isReply(emailType) {
  return !!(emailType && emailType.toLowerCase() === "reply");
}
